/**
 * Favourite IPL franchise picks for profile grid. Logos live at
 * `favs/cricket_teams/{slug}.png` on cricket8-web-content.
 */
export const PROFILE_FAV_IPL_LOGO_BASE =
  "https://cricket8-web-content.s3.us-east-1.amazonaws.com/favs/cricket_teams/";

/** Grid order: row 1 left→right, then row 2 (matches profile picker layout). */
export const PROFILE_FAV_IPL_TEAM_OPTIONS = [
  { slug: "chennai-super-kings", label: "CSK" },
  { slug: "delhi-capitals", label: "DC" },
  { slug: "gujarat-titans", label: "GT" },
  { slug: "kolkata-knight-riders", label: "KKR" },
  { slug: "lucknow-super-giants", label: "LSG" },
  { slug: "mumbai-indians", label: "MI" },
  { slug: "punjab-kings", label: "PBKS" },
  { slug: "rajasthan-royals", label: "RR" },
  { slug: "royal-challengers-bangalore", label: "RCB" },
  { slug: "sunrisers-hyderabad", label: "SRH" },
] as const;

export type ProfileFavIplSlug = (typeof PROFILE_FAV_IPL_TEAM_OPTIONS)[number]["slug"];

const OPTION_SLUGS = PROFILE_FAV_IPL_TEAM_OPTIONS.map((t) => t.slug);
const SLUG_SET = new Set<string>(OPTION_SLUGS);
const SLUG_TO_LABEL: Record<string, string> = Object.fromEntries(
  PROFILE_FAV_IPL_TEAM_OPTIONS.map((t) => [t.slug, t.label])
);

/** Full franchise names for expanded favourites / tooltips (grid tiles use short `label`). */
const SLUG_TO_LONG_LABEL: Record<string, string> = {
  "chennai-super-kings": "Chennai Super Kings",
  "delhi-capitals": "Delhi Capitals",
  "gujarat-titans": "Gujarat Titans",
  "kolkata-knight-riders": "Kolkata Knight Riders",
  "lucknow-super-giants": "Lucknow Super Giants",
  "mumbai-indians": "Mumbai Indians",
  "punjab-kings": "Punjab Kings",
  "rajasthan-royals": "Rajasthan Royals",
  "royal-challengers-bangalore": "Royal Challengers Bangalore",
  "sunrisers-hyderabad": "Sunrisers Hyderabad",
};

export function iplTeamLogoUrl(slug: string): string {
  return `${PROFILE_FAV_IPL_LOGO_BASE}${encodeURIComponent(slug)}.png`;
}

/** IPL logos that need a white tile for legibility on purple backgrounds. */
const IPL_WHITE_TILE_SLUGS = new Set<string>(["chennai-super-kings"]);

export function iplLogoUsesWhiteTile(slug: string): boolean {
  return IPL_WHITE_TILE_SLUGS.has(slug);
}

export function parseFavIplFromStorage(raw: string | undefined | null): string[] {
  if (!raw?.trim()) return [];
  const parts = raw
    .split(/,\s*/)
    .map((s) => s.trim())
    .filter(Boolean);
  const ordered: string[] = [];
  for (const slug of OPTION_SLUGS) {
    if (parts.includes(slug)) ordered.push(slug);
  }
  for (const p of parts) {
    if (!SLUG_SET.has(p) && !ordered.includes(p)) ordered.push(p);
  }
  return ordered;
}

export function serializeFavIplToStorage(selected: readonly string[]): string {
  if (selected.length === 0) return "";
  const set = new Set(selected);
  const known = OPTION_SLUGS.filter((s) => set.has(s));
  const unknown = selected.filter((s) => !SLUG_SET.has(s));
  return [...known, ...unknown].join(", ");
}

export function favIplLabel(slug: string): string {
  return SLUG_TO_LABEL[slug] ?? slug.replace(/-/g, " ");
}

export function favIplLongLabel(slug: string): string {
  return SLUG_TO_LONG_LABEL[slug] ?? favIplLabel(slug);
}
